/**
 * MUI Component Style Overrides
 *
 * Centralizes component-specific styling that was previously scattered across files.
 * These overrides apply globally to all instances of MUI components.
 */

import colors from './colors';

const components: any = {
  // Tabs — clean sans-serif, sentence case, mid-weight. Removes
  // MUI's default ALL-CAPS treatment so tab strips read like the
  // reference design ("Overview / Clients / Courses") rather than
  // shouty labels.
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.9375rem',
        letterSpacing: 0,
        minHeight: 44,
        // Match the heavier "active tab" feel from the reference
        // while keeping inactive tabs muted.
        '&.Mui-selected': {
          fontWeight: 700,
        },
      },
    },
  },

  // Overline = SCOPE-style field label. Pulls the muted grey by
  // default so call sites don't have to remember `color="text.secondary"`.
  MuiTypography: {
    styleOverrides: {
      overline: {
        color: colors.text?.secondary || 'rgba(0,0,0,0.6)',
        display: 'inline-block',
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        textAlign: 'left', // Override global text-align: center from App
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
            borderColor: colors.secondary.main, // Black focus border
          },
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: colors.secondary.main, // Black focus label
        },
      },
    },
    defaultProps: {
      variant: 'outlined',
      fullWidth: true,
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        '&.Mui-focused': {
          color: colors.secondary.main, // Black focus label
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.secondary.main, // Black focus border
        },
      },
    },
  },

  // Let the dropdown popper size to its content instead of inheriting the input width.
  // Filters with narrow inputs (e.g., "Group", "Course") were truncating option labels.
  // - placement: anchors top-left of popper to bottom-left of input
  // - modifiers.computeStyles.adaptive=false: Popper.js positions via left/top rather
  //   than transform: translate3d, so width changes don't shift the popper sideways
  MuiAutocomplete: {
    defaultProps: {
      slotProps: {
        popper: {
          placement: 'bottom-start',
          modifiers: [
            { name: 'computeStyles', options: { adaptive: false } },
          ],
          sx: {
            width: 'auto !important',
            minWidth: 320,
          },
        },
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none', // Disable uppercase transformation
        borderRadius: 8,
        fontWeight: 500,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        border: `1px solid ${colors.divider}`,
        boxShadow: 'none',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: colors.shadow.card,
        },
      },
    },
  },

  MuiCardMedia: {
    styleOverrides: {
      root: {
        transition: 'transform 0.3s ease-in-out',
        '.MuiCard-root:hover &': {
          transform: 'scale(1.05)',
        },
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        width: 240,
        boxSizing: 'border-box',
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'scale(1.1)',
        },
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        borderBottom: `1px solid ${colors.divider}`,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },

  MuiStepper: {
    styleOverrides: {
      root: {
        '& .MuiStepIcon-root': {
          color: 'gray', // default (inactive) step color
        },
        '& .MuiStepIcon-root.Mui-active': {
          color: colors.secondary.main, // active step circle color (black)
        },
        '& .MuiStepIcon-root.Mui-completed': {
          color: colors.secondary.main, // completed step color (black)
        },
      },
    },
  },

  // Override for focus/selected states on list items
  MuiListItemButton: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          backgroundColor: colors.action.selected,
          '&:hover': {
            backgroundColor: colors.action.hover,
          },
        },
      },
    },
  },

  // Override table row hover to use light gray instead of blue
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&.MuiTableRow-hover:hover': {
          backgroundColor: 'rgb(240, 240, 240)', // Light gray matching navbar
        },
      },
    },
  },
};

export default components;
